import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';

export const incomingAiInvoicesApi = createApi({
  reducerPath: 'incomingAiInvoicesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${config.apiUrl}/incoming-ai-invoices`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.user?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['IncomingAiInvoices', 'IncomingAiInvoice'],
  endpoints: (builder) => ({
    listIncomingAiInvoices: builder.query({
      query: (params) => ({ url: '/', params }),
      providesTags: [{ type: 'IncomingAiInvoices', id: 'LIST' }],
    }),
    createIncomingAiInvoice: builder.mutation({
      query: (body) => ({ url: '/', method: 'POST', body }),
      invalidatesTags: [{ type: 'IncomingAiInvoices', id: 'LIST' }],
    }),
    getIncomingAiInvoice: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, err, id) => [{ type: 'IncomingAiInvoice', id }],
    }),
    getIncomingAiInvoiceImages: builder.query({
      query: (id) => `/${id}/images`,
      providesTags: (result, err, id) => [{ type: 'IncomingAiInvoice', id }],
    }),
    recognizeIncomingAiInvoice: builder.mutation({
      query: (id) => ({ url: `/${id}/recognize`, method: 'POST' }),
      invalidatesTags: (result, err, id) => [
        { type: 'IncomingAiInvoices', id: 'LIST' },
        { type: 'IncomingAiInvoice', id },
      ],
    }),
    replaceIncomingAiInvoiceImages: builder.mutation({
      query: ({ id, images }) => ({ url: `/${id}/images`, method: 'POST', body: { images } }),
      invalidatesTags: (result, err, arg) => [
        { type: 'IncomingAiInvoices', id: 'LIST' },
        { type: 'IncomingAiInvoice', id: arg.id },
      ],
    }),
    structureIncomingAiInvoice: builder.mutation({
      query: (id) => ({ url: `/${id}/structure`, method: 'POST' }),
      invalidatesTags: (result, err, id) => [
        { type: 'IncomingAiInvoices', id: 'LIST' },
        { type: 'IncomingAiInvoice', id },
      ],
    }),
    matchIncomingAiInvoiceProducts: builder.mutation({
      query: (id) => ({ url: `/${id}/match-products`, method: 'POST' }),
      invalidatesTags: (result, err, id) => [
        { type: 'IncomingAiInvoices', id: 'LIST' },
        { type: 'IncomingAiInvoice', id },
      ],
    }),
    updateIncomingAiInvoiceLine: builder.mutation({
      query: ({ id, lineId, ...body }) => ({
        url: `/${id}/lines/${lineId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, err, arg) => [
        { type: 'IncomingAiInvoices', id: 'LIST' },
        { type: 'IncomingAiInvoice', id: arg.id },
      ],
    }),
    finishIncomingAiInvoice: builder.mutation({
      query: (id) => ({ url: `/${id}/finish`, method: 'POST' }),
      invalidatesTags: (result, err, id) => [
        { type: 'IncomingAiInvoices', id: 'LIST' },
        { type: 'IncomingAiInvoice', id },
      ],
    }),
  }),
});

export const {
  useListIncomingAiInvoicesQuery,
  useCreateIncomingAiInvoiceMutation,
  useGetIncomingAiInvoiceQuery,
  useGetIncomingAiInvoiceImagesQuery,
  useRecognizeIncomingAiInvoiceMutation,
  useReplaceIncomingAiInvoiceImagesMutation,
  useStructureIncomingAiInvoiceMutation,
  useMatchIncomingAiInvoiceProductsMutation,
  useUpdateIncomingAiInvoiceLineMutation,
  useFinishIncomingAiInvoiceMutation,
} = incomingAiInvoicesApi;
